import React from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { clientProductsFor } from '../lib/accessProfiles';
import { Building2, FileText, Send, Search, Users, SlidersHorizontal, List } from 'lucide-react';

const MODULE_ICONS = {
  customers: Users,
  processings: List,
  'api-sends': Send,
  parameters: SlidersHorizontal,
  upload: FileText,
  maxpar: Search
};

const MODULE_COPY = {
  customers: 'Veja se o cliente está regular e se a apólice está vigente',
  processings: 'Consulte os arquivos processados na tela e os que chegaram por e-mail',
  'api-sends': 'Monitore os envios para a MAXPAR e gere o relatório do arquivo',
  parameters: 'Escolha percentual ou valor fixo, no arquivo processado ou na base vigente',
  upload: 'Faça upload do arquivo mensal e baixe o Excel de conferência ou a fatura em PDF',
  maxpar: 'Consulte as apólices enviadas para a MAXPAR'
};

/**
 * Builds the Guaibim product cards the signed-in profile is allowed to open.
 */
function productsForRole(role) {
  return clientProductsFor(role).map((product) => ({
    name: product.label,
    description: product.maxpar
      ? 'Arquivo mensal de apólices, consulta MAXPAR, Excel e fatura'
      : 'Processamento do CSV de assistência',
    modules: product.links.map((link) => ({
      name: link.label,
      path: link.path,
      icon: MODULE_ICONS[link.key] || FileText,
      description: link.key === 'upload' && !product.maxpar
        ? 'Envie o CSV e veja o valor a faturar, ativos e cancelados'
        : MODULE_COPY[link.key]
    }))
  }));
}

const Clients = () => {
  const { user } = useAuth();
  const products = productsForRole(user?.role);

  return (
    <Layout 
      headerTitle="Clientes" 
      headerSubtitle="Gerenciamento de clientes e suas funcionalidades"
    >
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Clientes</h1>
            <p className="text-gray-600">
              Gerencie seus clientes e acesse suas funcionalidades específicas
            </p>
          </div>

          <div className="grid gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-primary-100 rounded-lg mr-4">
                  <Building2 className="h-8 w-8 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Guaibim</h2>
                  <p className="text-gray-600">Produtos de integração do cliente Guaibim</p>
                </div>
              </div>

              {products.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Seu perfil pode abrir esta área, sem as ações de cada produto.
                </p>
              ) : (
                <div className="space-y-6">
                  {products.map((product) => (
                    <div key={product.name}>
                      <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                      <div className="grid md:grid-cols-3 gap-4">
                        {product.modules.map((module) => (
                          <div
                            key={module.path}
                            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                            onClick={() => window.location.href = module.path}
                          >
                            <div className="flex items-center mb-3">
                              <div className="p-2 bg-gray-100 rounded-lg mr-3">
                                <module.icon className="h-5 w-5 text-gray-600" />
                              </div>
                              <h4 className="font-medium text-gray-900">{module.name}</h4>
                            </div>
                            <p className="text-sm text-gray-600">{module.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Clients;
